// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback } from 'react';
import api from '../api';

/**
 * Full-page error display for critical errors (auth, access denied, not found, etc.)
 */
function ErrorPage({
  statusCode = 500,
  title,
  message,
  details = null,
  showLogin = false,
  showHome = true,
  showRetry = false,
  onRetry = null
}) {
  const handleLogin = useCallback(() => {
    window.location.href = api.getLoginUrl();
  }, []);

  const handleGoHome = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleGoBack = useCallback(() => {
    window.history.back();
  }, []);

  // Determine icon and color based on status code
  const getIconAndColor = () => {
    if (statusCode === 401) {
      return {
        color: 'text-st-warning',
        bgColor: 'bg-st-warning/10',
        icon: (
          <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <circle cx="12" cy="16" r="1"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        )
      };
    }
    if (statusCode === 403) {
      return {
        color: 'text-st-error',
        bgColor: 'bg-st-error/10',
        icon: (
          <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
          </svg>
        )
      };
    }
    if (statusCode === 404) {
      return {
        color: 'text-st-accent',
        bgColor: 'bg-st-accent/10',
        icon: (
          <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="11" y1="8" x2="11" y2="14"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
        )
      };
    }
    // Server errors (5xx)
    return {
      color: 'text-st-error',
      bgColor: 'bg-st-error/10',
      icon: (
        <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      )
    };
  };

  const { color, bgColor, icon } = getIconAndColor();

  // Default titles and messages based on status code
  const defaultContent = {
    400: { title: 'Bad Request', message: 'The request could not be understood.' },
    401: { title: 'Authentication Required', message: 'Please sign in with GitLab to continue.' },
    403: { title: 'Access Denied', message: "You don't have permission to access this resource." },
    404: { title: 'Not Found', message: "The page you're looking for doesn't exist." },
    500: { title: 'Server Error', message: 'Something went wrong. Please try again later.' },
    502: { title: 'Bad Gateway', message: 'The server is temporarily unavailable.' },
    503: { title: 'Service Unavailable', message: 'The service is temporarily unavailable.' },
  };

  const displayTitle = title || defaultContent[statusCode]?.title || 'Error';
  const displayMessage = message || defaultContent[statusCode]?.message || 'An unexpected error occurred.';

  return (
    <div className="min-h-screen bg-st-primary flex items-center justify-center p-4">
      <div className="bg-st-secondary border-2 border-st-border p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-4">
          <svg viewBox="0 0 100 100" className="w-16 h-16 mx-auto opacity-50">
            <rect x="5" y="5" width="90" height="90" fill="rgb(var(--st-bg-primary))" stroke="#FF6600" strokeWidth="3"/>
            <g fill="#FF6600" opacity="0.5">
              <rect x="20" y="35" width="25" height="3"/>
              <rect x="55" y="35" width="25" height="3"/>
              <rect x="20" y="62" width="25" height="3"/>
              <rect x="55" y="62" width="25" height="3"/>
            </g>
            <rect x="35" y="40" width="30" height="20" fill="#1A1614" opacity="0.5"/>
          </svg>
        </div>

        {/* Error Icon */}
        <div className={`${color} mb-4 flex justify-center`}>
          {icon}
        </div>

        {/* Status Code */}
        <div className={`text-6xl font-display font-bold ${color} mb-2`}>
          {statusCode}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-display font-semibold text-st-text mb-3">
          {displayTitle}
        </h1>

        {/* Message */}
        <p className="text-st-muted mb-4 font-body">
          {displayMessage}
        </p>

        {/* Details (for debugging) */}
        {details && (
          <div className={`${bgColor} p-3 mb-6 text-left`}>
            <p className="text-sm text-st-muted font-mono break-all">
              {details}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          {showLogin && (
            <button
              onClick={handleLogin}
              className="btn bg-st-accent hover:bg-st-accent-hover text-white px-6 py-2.5 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 01-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 014.82 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0118.6 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.51L23 13.45a.84.84 0 01-.35.94z"/>
              </svg>
              Sign in with GitLab
            </button>
          )}

          {showRetry && onRetry && (
            <button
              onClick={onRetry}
              className="btn bg-st-accent hover:bg-st-accent-hover text-white px-6 py-2.5 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          )}

          {showHome && (
            <button
              onClick={handleGoHome}
              className="btn btn-secondary px-6 py-2.5"
            >
              Go to Dashboard
            </button>
          )}

          <button
            onClick={handleGoBack}
            className="btn bg-transparent border-2 border-st-border-subtle hover:bg-st-tertiary text-st-muted px-6 py-2.5"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorPage;
